import { keepPreviousData } from '@tanstack/react-query';
import {
    OpenPayoutDetailParameters,
    Payout,
    PayoutDetailParameters,
    PayoutDetailSummaryParameters,
    useRepositoryMutation,
    useRepositoryQuery,
    useRepositorySuspenseQuery,
} from '@mews-commander/data-access';
import { EnterpriseLink } from '@mews-commander/ui';
import { useCommanderSelector } from '@mews-commander/utils';
import { LocalizedText, lt, showSuccessAlert, useAppClockService } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { TableState } from '@optimus-web/core';
import { CombinedSchemaType } from './components/scheduledExport/schema';
import { getFilters } from './selectors';

export const useGetPayouts = ({ pagination }: { pagination: TableState<Payout>['pagination'] }) => {
    const appClockService = useAppClockService();
    const { startUtc, endUtc } = useCommanderSelector((state) => getFilters(state, { appClockService }));

    return useRepositoryQuery(
        'payouts',
        'getAll',
        {
            pagination,
            createdUtc: { startUtc, endUtc },
        },
        { placeholderData: keepPreviousData }
    );
};

export const useExportPayouts = () => {
    const appClockService = useAppClockService();

    const { startUtc, endUtc } = useCommanderSelector((state) => getFilters(state, { appClockService }));

    return useRepositoryMutation('payouts', 'export', {
        transformParams: ({ pagination }: { pagination: TableState<Payout>['pagination'] }) => ({
            pagination,
            createdUtc: { startUtc, endUtc },
        }),
        onSuccess: () => {
            showSuccessAlert({
                title: lt('PayoutExportQueuedSuccessMessage'),
                action: (
                    <EnterpriseLink to="Export/Index" search={{ tab: 'history' }} inherit>
                        <LocalizedText textKey={TextKey.PayoutExportDetailsRedirect} />
                    </EnterpriseLink>
                ),
            });
        },
    });
};

export const useExportPayoutDetail = () =>
    useRepositoryMutation('payoutDetail', 'export', {
        transformParams: ({ payoutId }: { payoutId: string }) => ({
            payoutId,
        }),
        onSuccess: () => {
            showSuccessAlert({
                description: lt('ExportQueuedSuccessMessage'),
            });
        },
    });

export const useScheduleExportPayoutReport = () =>
    useRepositoryMutation('payoutReport', 'scheduleExport', {
        transformParams: (params) => ({ ...params }),
    });

export const useUpdateScheduleExportPayoutReport = () =>
    useRepositoryMutation('payoutReport', 'updateScheduleExport', {
        transformParams: (params) => ({ ...params }),
    });

export const useGetPayoutSummaries = () => {
    const appClockService = useAppClockService();
    const { startUtc, endUtc } = useCommanderSelector((state) => getFilters(state, { appClockService }));

    return useRepositoryQuery(
        'payoutSummaries',
        'getAll',
        { createdUtc: { startUtc, endUtc } },
        { placeholderData: keepPreviousData }
    );
};

export const useGetPayoutDetailSummary = ({ payoutId }: PayoutDetailSummaryParameters) =>
    useRepositoryQuery('payoutDetailSummaries', 'getAll', { payoutId }, { placeholderData: keepPreviousData });

export const useGetOpenPayouts = () =>
    useRepositoryQuery('openPayouts', 'getAll', {}, { placeholderData: keepPreviousData });

export const useGetPayoutTransactions = ({ payoutId, pagination }: PayoutDetailParameters) =>
    useRepositoryQuery('payoutTransactions', 'getAll', { payoutId, pagination }, { placeholderData: keepPreviousData });

export const useGetOpenPayoutSummary = () =>
    useRepositoryQuery('openPayouts', 'getAll', {}, { placeholderData: keepPreviousData });

export const useGetOpenPayoutTransactions = ({ currencyCode, pagination }: OpenPayoutDetailParameters) =>
    useRepositoryQuery(
        'openPayoutTransactions',
        'getAll',
        { currencyCode, pagination },
        { placeholderData: keepPreviousData }
    );

export const useGetExportTargets = () =>
    useRepositoryQuery('exportTargets', 'getAll', {}, { placeholderData: keepPreviousData });

export const useGetScheduleExport = (exportScheduleId: string) =>
    useRepositorySuspenseQuery(
        'payoutReport',
        'getScheduleExport',
        { exportScheduleId },
        {
            select: (scheduledExport): CombinedSchemaType => ({
                name: scheduledExport.name,
                reportInterval: scheduledExport.reportInterval,
                format: scheduledExport.format,
                frequency: scheduledExport.frequency,
                startUtc: scheduledExport.startUtc,
                exportTargetId: scheduledExport.exportTargetId ?? null,
                isEnabled: !scheduledExport.isEnabled,
                notifyCreatorAboutExport: scheduledExport.notifyCreatorAboutExport,
                includePayoutDetails: scheduledExport.includePayoutDetails,
            }),
        }
    );
